import { ArrowPathRoundedSquareIcon, WrenchIcon, EyeIcon, EyeSlashIcon, FolderIcon, FolderOpenIcon, QuestionMarkCircleIcon, Squares2X2Icon } from '@heroicons/react/24/outline';
import classNames from 'classnames';
import { memo, useState } from 'react';
import type { ObjectGroup, ObjectShape } from 'shared-types';
import { T, useT } from '../../../translation/src';
import { groupSelection, ungroupSelection } from '../document/actions';
import { updateGroup, updateShape } from '../document/specOps';
import { ContextMenu, useCloseContextMenu } from '../../../components/ContextMenu';
import { SelectItemContent } from '../../../components/selects/SelectItem';
import { useEditorState, useObjectEditor } from '../EditorContext';
import { useSelectionActions } from '../useSelectionActions';
import { PanelButton, PanelSection } from './fields';

/** A shape's display name: its own, or "<kind> <n>". */
export function useShapeLabel(): (shape: ObjectShape, index: number) => string {
  const labels = {
    polyline: useT('line', { swc: true }),
    rect: useT('rectangle', { swc: true }),
    circle: useT('circle', { swc: true }),
    arc: useT('arc', { swc: true }),
    path: useT('curve', { swc: true }),
  } satisfies Record<ObjectShape['kind'], string>;
  return (shape, index) => shape.name || `${labels[shape.kind]} ${index + 1}`;
}

const InlineName: React.FC<{ value: string; onRename: (name: string) => void; className?: string }> = ({ value, onRename, className }) => {
  const [editing, setEditing] = useState<string | null>(null);
  if (editing === null)
    return (
      <span className={classNames('truncate', className)} onDoubleClick={() => setEditing(value)}>
        {value}
      </span>
    );
  const commit = () => {
    const next = editing.trim();
    setEditing(null);
    if (next && next !== value) onRename(next);
  };
  return (
    <input
      autoFocus
      value={editing}
      onChange={(e) => setEditing(e.target.value)}
      onBlur={commit}
      onClick={(e) => e.stopPropagation()}
      onKeyDown={(e) => {
        if (e.key === 'Enter') commit();
        if (e.key === 'Escape') setEditing(null);
      }}
      maxLength={80}
      className="min-w-0 flex-1 rounded border border-french-violet-600 bg-white px-1 text-sm dark:bg-[#262626]"
    />
  );
};

/** The selection's actions as a right-click list (the same ones the canvas's menu offers). */
const SelectionMenuItems: React.FC = () => {
  const actions = useSelectionActions();
  const close = useCloseContextMenu();
  return (
    <div className="flex flex-col">
      {actions.map((action) => (
        <SelectItemContent
          key={action.id}
          icon={action.icon}
          onClick={() => {
            close();
            action.run();
          }}
          className={action.danger ? 'text-error-red-500' : undefined}
        >
          <span className="flex flex-1 items-center justify-between gap-3">
            {action.label}
            {action.shortcut && <span className="text-xs text-gray-400">{action.shortcut}</span>}
          </span>
        </SelectItemContent>
      ))}
    </div>
  );
};

/** Groups (with their shapes) and loose shapes; click selects, double-click renames, the eye hides. */
const LayersPanel_: React.FC = () => {
  const { store } = useObjectEditor();
  const { spec, selection } = useEditorState();
  const shapeLabel = useShapeLabel();
  const [collapsed, setCollapsed] = useState<ReadonlySet<string>>(new Set());
  const hideLabel = useT('hide', { swc: true });
  const showLabel = useT('show', { swc: true });
  const defaultGroupName = useT('group', { swc: true });

  const selectedShapeIds = selection.kind === 'shapes' ? selection.ids : [];
  const grouped = new Set(spec.groups.flatMap((g) => g.shapeIds));
  const loose = spec.shapes.filter((s) => !grouped.has(s.id));
  const indexOf = (shape: ObjectShape) => spec.shapes.indexOf(shape);

  const selectShape = (e: React.MouseEvent, id: string) => {
    const ids = e.shiftKey || e.metaKey || e.ctrlKey ? (selectedShapeIds.includes(id) ? selectedShapeIds.filter((x) => x !== id) : [...selectedShapeIds, id]) : [id];
    store.select(ids.length ? { kind: 'shapes', ids } : { kind: 'none' });
  };

  const makeGroup = () => groupSelection(store, `${defaultGroupName} ${spec.groups.length + 1}`);

  const shapeRow = (shape: ObjectShape, nested: boolean) => (
    <li key={shape.id}>
      <ContextMenu
        buttonChild={
          <div
            onClick={(e) => selectShape(e, shape.id)}
            // Right-click acts on what is right-clicked: a selection holding it stays (batch actions).
            onContextMenu={() => !selectedShapeIds.includes(shape.id) && store.select({ kind: 'shapes', ids: [shape.id] })}
            className={classNames(
              'group/row flex cursor-pointer items-center gap-2 rounded px-2 py-1 text-sm',
              nested && 'ml-5',
              selectedShapeIds.includes(shape.id) ? 'bg-neon-green-300 text-black' : 'hover:bg-gray-100 dark:hover:bg-black',
              shape.hidden && 'opacity-50',
            )}
          >
            <span className={classNames('h-0.5 w-3 shrink-0', shape.style === 'dashed' ? 'border-t border-dashed border-current' : 'bg-current', shape.layer === 'selection' && 'text-sky-600')} />
            <InlineName value={shapeLabel(shape, indexOf(shape))} onRename={(name) => store.apply((s) => updateShape(s, shape.id, (x) => ({ ...x, name })))} className="flex-1" />
            <button
              type="button"
              aria-label={shape.hidden ? showLabel : hideLabel}
              title={shape.hidden ? showLabel : hideLabel}
              onClick={(e) => {
                e.stopPropagation();
                store.apply((s) => updateShape(s, shape.id, (x) => ({ ...x, hidden: !x.hidden })));
              }}
              className={classNames('shrink-0', !shape.hidden && 'opacity-0 group-hover/row:opacity-100')}
            >
              {shape.hidden ? <EyeSlashIcon className="h-4 w-4" /> : <EyeIcon className="h-4 w-4" />}
            </button>
          </div>
        }
      >
        <SelectionMenuItems />
      </ContextMenu>
    </li>
  );

  const groupRow = (group: ObjectGroup) => {
    const open = !collapsed.has(group.id);
    const selected = selection.kind === 'group' && selection.id === group.id;
    const shapes = group.shapeIds.map((id) => spec.shapes.find((s) => s.id === id)).filter((s): s is ObjectShape => !!s);
    return (
      <li key={group.id}>
        <ContextMenu
          buttonChild={
            <div
              onClick={() => store.select({ kind: 'group', id: group.id })}
              onContextMenu={() => store.select({ kind: 'group', id: group.id })}
              className={classNames('flex cursor-pointer items-center gap-2 rounded px-2 py-1 text-sm font-medium', selected ? 'bg-neon-green-300 text-black' : 'hover:bg-gray-100 dark:hover:bg-black')}
            >
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  setCollapsed((c) => {
                    const next = new Set(c);
                    if (open) next.add(group.id);
                    else next.delete(group.id);
                    return next;
                  });
                }}
              >
                {open ? <FolderOpenIcon className="h-4 w-4" /> : <FolderIcon className="h-4 w-4" />}
              </button>
              <InlineName value={group.name} onRename={(name) => store.apply((s) => updateGroup(s, group.id, (g) => ({ ...g, name })))} className="flex-1" />
              {group.repeat && <ArrowPathRoundedSquareIcon className="h-4 w-4 shrink-0 text-french-violet-600" aria-label="repeat" />}
              {group.modifiers?.some((m) => m.enabled !== false) && <WrenchIcon className="h-4 w-4 shrink-0 text-sky-600 dark:text-sky-400" aria-label="modifiers" />}
              {group.visibleWhen && <QuestionMarkCircleIcon className="h-4 w-4 shrink-0 text-amber-600" aria-label="conditional" />}
            </div>
          }
        >
          <SelectionMenuItems />
        </ContextMenu>
        {open && <ul>{shapes.map((shape) => shapeRow(shape, true))}</ul>}
      </li>
    );
  };

  return (
    <PanelSection
      title={<T _str="layers" />}
      actions={
        <div className="flex gap-1">
          <PanelButton onClick={makeGroup} disabled={selectedShapeIds.length === 0} title="Ctrl+G">
            <Squares2X2Icon className="h-3.5 w-3.5" />
            <T _str="group" swc />
          </PanelButton>
          {selection.kind === 'group' && (
            <PanelButton onClick={() => ungroupSelection(store)}>
              <T _str="ungroup" swc />
            </PanelButton>
          )}
        </div>
      }
    >
      {spec.shapes.length === 0 ? (
        <p className="py-4 text-center text-sm text-gray-500 dark:text-gray-400">
          <T _str="draw a shape or import a drawing to start" swc />
        </p>
      ) : (
        <ul className="flex flex-col gap-0.5">
          {spec.groups.map(groupRow)}
          {loose.map((shape) => shapeRow(shape, false))}
        </ul>
      )}
    </PanelSection>
  );
};

export const LayersPanel = memo(LayersPanel_);
